import React from "react";
import { Dialog, Popover } from "@mui/material";
import { ActivityLevel, TravelStyleDetail } from "@/types/dto/pdp/pdp-header-about";
import RequestAQuoteSection from "@/components/forms/request-a-quote/request-a-quote-section";
import TravelStyleModal from "@/components/product-detail-page/modals/travel-styles/travel-style-modal";
import ActivityLevelModal from "@/components/product-detail-page/modals/activity-level/activity-level-modal";
import OnTourAirModal from "@/components/product-detail-page/modals/on-tour-air/on-tour-air-modal";
import { BasicInfo } from "@/types/dto/common/basic-info";
import { ExtensionStyle } from "@/types/dto/pdp/pdp-itinerary";
import useScrollLock from "@/hooks/scroll-lock/use-scroll-lock";
import PrintModal from "./modals/itinerary-summary/print-modal";
import BestTimeToTravelModal from "./modals/itinerary-summary/best-time-to-travel-modal";
import OfferTermsModal from "./modals/offer-terms/offer-terms-modal";
import ExtensionStylesModal from "./modals/extension-styles/extension-styles-modal";
import ShareLinkModal from "./modals/itinerary-summary/share-link-modal";

interface RAQModalProps {
  tourId: string;
  title: string;
  subtitle?: string;
  tourDates?: string[];
  landingPageId?: string;
  selectedDate: string;
}

interface ProductDetailPageModalsProps {
  activityLevelIsOpen: boolean;
  handleCloseActivityLevel: () => void;
  travelStylesIsOpen: boolean;
  handleCloseTravelStyles: () => void;
  onTourAirIsOpen: boolean;
  handleCloseOnTourAir: () => void;
  requestAQuoteIsOpen: boolean;
  handleCloseRequestAQuote: () => void;
  requestAQuoteProps: RAQModalProps;
  printIsOpen: boolean;
  handleClosePrint: () => void;
  printProps: {
    tourName: string;
    dateRange: string;
    status: "building" | "ready" | "error";
    handleOpen: () => void;
  };
  shareIsOpen: boolean;
  handleCloseShare: () => void;
  shareButtonRef?: React.RefObject<HTMLElement | null>;
  bestTimeToTravelIsOpen: boolean;
  handleCloseBestTimeToTravel: () => void;
  offerTermsIsOpen: boolean;
  handleCloseOfferTerms: () => void;
  offerTermsProps?: {
    offerTitle?: string;
    termsAndConditions?: string;
  };
  travelStylesProps: {
    travelStyles: TravelStyleDetail[]
    tourTravelStyles: TravelStyleDetail["name"][]
  };
  activityLevelProps: {
    activityLevelDetails: ActivityLevel[];
    activityLevelNumber: ActivityLevel["number"];
    openTo: ActivityLevel["number"];
  };
  onTourAir: BasicInfo;
  bestTimeToTravelProps: BasicInfo;
  extensionStylesIsOpen: boolean;
  handleCloseExtensionStyles: () => void;
  extensionStyleProps: ExtensionStyle[];
  currentExtensionStyle: string;
}

const ProductDetailPageModals = ({
  activityLevelIsOpen,
  handleCloseActivityLevel,
  travelStylesIsOpen,
  handleCloseTravelStyles,
  onTourAirIsOpen,
  handleCloseOnTourAir,
  requestAQuoteIsOpen,
  handleCloseRequestAQuote,
  requestAQuoteProps,
  printIsOpen,
  handleClosePrint,
  printProps,
  shareIsOpen,
  handleCloseShare,
  shareButtonRef,
  bestTimeToTravelIsOpen,
  handleCloseBestTimeToTravel,
  offerTermsIsOpen,
  handleCloseOfferTerms,
  offerTermsProps,
  travelStylesProps,
  activityLevelProps,
  onTourAir,
  bestTimeToTravelProps,
  extensionStylesIsOpen,
  handleCloseExtensionStyles,
  extensionStyleProps,
  currentExtensionStyle
}: ProductDetailPageModalsProps) => {
  const isAnyModalOpen =
    activityLevelIsOpen ||
    travelStylesIsOpen ||
    onTourAirIsOpen ||
    requestAQuoteIsOpen ||
    printIsOpen ||
    shareIsOpen ||
    bestTimeToTravelIsOpen ||
    offerTermsIsOpen ||
    extensionStylesIsOpen;

    useScrollLock(isAnyModalOpen);

  return (
    <div>
      <Dialog
        open={activityLevelIsOpen}
        onClose={handleCloseActivityLevel}
        disableScrollLock
        maxWidth="md"
        data-testid="pdp-activity-level-modal"
      >
        <ActivityLevelModal
          activityLevels={activityLevelProps.activityLevelDetails}
          handleClose={handleCloseActivityLevel}
          thisTourNumber={activityLevelProps.activityLevelNumber}
          openTo={activityLevelProps.openTo}
        />
      </Dialog>
      <Dialog
        open={travelStylesIsOpen}
        onClose={handleCloseTravelStyles}
        disableScrollLock
        maxWidth="md"
        data-testid="pdp-travel-styles-modal"
      >
        <TravelStyleModal
          travelStyles={travelStylesProps.travelStyles}
          handleClose={handleCloseTravelStyles}
          thisTourTravelStyle={travelStylesProps.tourTravelStyles}
        />
      </Dialog>
      <Dialog
        open={onTourAirIsOpen}
        onClose={handleCloseOnTourAir}
        disableScrollLock
        maxWidth="md"
        data-testid="pdp-on-tour-air-modal"
      >
        <OnTourAirModal
          header={onTourAir.headerText ?? ""}
          description={onTourAir.copyText ?? ""}
          handleClose={handleCloseOnTourAir}
        />
      </Dialog>
      {/* RAQ */}
      <Dialog
        open={requestAQuoteIsOpen}
        onClose={handleCloseRequestAQuote}
        disableScrollLock
        maxWidth="md"
        data-testid="pdp-raq-modal"
      >
        <RequestAQuoteSection
          handleClose={handleCloseRequestAQuote}
          tourId={requestAQuoteProps.tourId}
          landingPageId={requestAQuoteProps.landingPageId}
          defaultDate={requestAQuoteProps.selectedDate}
          tourTitle={`${requestAQuoteProps.title} ${requestAQuoteProps.subtitle}`}
        />
      </Dialog>

      <Dialog
        open={printIsOpen}
        onClose={handleClosePrint}
        maxWidth={false}
        data-testid="pdp-print-modal"
        disableScrollLock
        PaperProps={{ elevation: 16 }}
        sx={{
          "& .MuiDialog-paper": {
            margin: "1.6rem",
            width: "min(44rem, calc(100vw - 3.2rem))",
            borderRadius: "2rem"
          }
        }}
      >
        <PrintModal handleClose={handleClosePrint} {...printProps} />
      </Dialog>
      
      {/* Share Modal as a Popover */}
      <Popover
        open={shareIsOpen}
        disableScrollLock
        anchorEl={shareButtonRef?.current || null}
        onClose={handleCloseShare}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        sx={{
          "& .MuiPopover-paper": {
            borderRadius: "1rem",
            maxWidth: { xs: "36rem", sm: "50rem" },
            width: "100%"            
          }
        }}
        data-testid="pdp-share-link-modal"
      > 
        <ShareLinkModal
          handleClose={handleCloseShare}
        />      
      </Popover>      
      <Dialog
        open={bestTimeToTravelIsOpen}
        onClose={handleCloseBestTimeToTravel}
        maxWidth="md"
        data-testid="pdp-best-time-to-travel-modal"
        disableScrollLock
      >
        <BestTimeToTravelModal
          handleClose={handleCloseBestTimeToTravel}
          bestTimeToTravelProps={bestTimeToTravelProps}
        />
      </Dialog>
      <OfferTermsModal
        isOpen={offerTermsIsOpen}
        onClose={handleCloseOfferTerms}
        offerTitle={offerTermsProps?.offerTitle}
        termsAndConditions={offerTermsProps?.termsAndConditions}
      />
      <Dialog
        open={extensionStylesIsOpen}
        onClose={handleCloseExtensionStyles}
        maxWidth="md"
        data-testid="pdp-itinerary-extension-styles"
        disableScrollLock
      >
        <ExtensionStylesModal
          handleClose={handleCloseExtensionStyles}
          extensionStyleProps={extensionStyleProps}
          currentExtensionStyle={currentExtensionStyle}
        />
      </Dialog>
    </div>
  );
};

export default ProductDetailPageModals;
